/**
 * Portal Links API Client (Story 14.2)
 *
 * Typed wrapper around apiGet/apiPost/apiPatch/apiDelete for the 6 portal-link endpoints.
 * Mirrors the server projection shape from apps/server/src/services/portal-links.ts.
 */

import { apiGet, apiPost, apiPatch, apiDelete } from "./apiClient";

export type PortalLinkStatus = "active" | "expired" | "revoked";
export type PortalLinkScope = "full" | "report";
export type PortalLinkExpirationPreset = "7d" | "30d" | "90d" | "never";

export interface PortalLinkProjection {
  id: string;
  clientId: string;
  label: string;
  scope: string;
  reportId: string | null;
  tokenPrefix: string;
  hiddenMetricsJson: string | null;
  hiddenIntegrationsJson: string | null;
  /** Server-computed: true iff the `__cost_efficiency__` sentinel is present in hiddenMetricsJson (Story 14.4). */
  costEfficiencyHidden: boolean;
  expiresAt: number | null;
  passwordProtected: boolean;
  createdBy: string | null;
  revokedAt: number | null;
  lastAccessedAt: number | null;
  accessCount: number;
  createdAt: number;
  updatedAt: number;
  status: PortalLinkStatus;
}

export interface PortalLinkWithToken extends PortalLinkProjection {
  /** Raw token — surfaced exactly once on create/regenerate. Treat as a secret. */
  token: string;
  /** Full constructed portal URL ready to copy and share. */
  url: string;
}

export type CreatePortalLinkInput =
  | {
      clientId: string;
      label: string;
      scope: "full";
      expiresAt: PortalLinkExpirationPreset;
      password?: string;
      hiddenMetrics?: string[];
      hiddenIntegrations?: string[];
    }
  | {
      clientId: string;
      label: string;
      scope: "report";
      reportId: string;
      expiresAt: PortalLinkExpirationPreset;
      password?: string;
      hiddenMetrics?: string[];
      hiddenIntegrations?: string[];
    };

export interface UpdatePortalLinkInput {
  label?: string;
  expiresAt?: PortalLinkExpirationPreset;
  hiddenMetrics?: string[];
  hiddenIntegrations?: string[];
}

/**
 * Story 14.4 — reserved metric sentinels whose meaning is expanded at filter
 * time by the server view-route. Mirrored from
 * apps/server/src/services/portal-links.ts `HIDDEN_METRIC_SENTINELS`.
 *
 * UI-only reference: the quick-toggle checkboxes in `PortalAccessControlsCard`
 * add/remove these values from `hiddenMetrics`. Keep both ends in sync — a
 * unit test asserts the string literals match.
 */
export const PORTAL_HIDDEN_METRIC_SENTINELS = {
  COST_EFFICIENCY: "__cost_efficiency__",
} as const;

/**
 * Mirror of server-side `COST_EFFICIENCY_METRIC_IDS` (Story 14.4 D25).
 * Used by `PortalAccessControlsCard` to hide the expanded metric keys from
 * the advanced list while the cost-efficiency quick toggle is ON — this
 * prevents the "I hid X via the toggle, why is X still an option I can check
 * in the advanced list?" UX glitch flagged in R1-H4.
 *
 * DRIFT GUARD: a unit test asserts this list matches the server's list
 * byte-for-byte. If a new cost-efficiency metric ships, update BOTH halves
 * and the drift test will hold the line.
 */
export const PORTAL_COST_EFFICIENCY_METRIC_IDS: readonly string[] = [
  "spend",
  "cpc",
  "cpm",
  "cost_per_conversion",
  "cost",
  "cost_per_all_conversions",
  "roas",
  "cost_per_unique_click",
  "cost_per_outbound_click",
  "cost_per_result",
  "cost_per_action_link_click",
  "cost_per_action_purchase",
  "cost_per_action_lead",
  "cost_per_thruplay",
  "cost_per_15_sec_video_view",
  "cost_per_2_sec_continuous_video_view",
  "cost_per_inline_post_engagement",
  "cost_per_qualified_lead",
  "cost_per_lead",
  "cost_per_1000_reached",
];

/**
 * Parse a hidden-metrics / hidden-integrations JSON string into an array of
 * strings. Safe against null, empty, malformed JSON, and non-array payloads —
 * always returns a fresh array, never throws. Used by EditShareLinkSheet to
 * initialize its state from the stored projection and by a future hook/UI that
 * needs to render the persisted filter list.
 */
export function parsePortalHiddenArray(json: string | null): string[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string");
  } catch {
    return [];
  }
}

/**
 * Multiset-equality that treats an initial `null` (no filters ever configured)
 * and a current empty array `[]` as identical — the PATCH route should NOT
 * receive an empty array when the user never touched the filters, because
 * the server would still run the update and bump `updated_at` needlessly.
 *
 * **Order-insensitive** (R1 fix H2): toggling chips in a different sequence
 * than the persisted order used to mark the form dirty and trigger a needless
 * PATCH. Compares as a multiset by sorting both sides before index-by-index
 * comparison.
 *
 * **Unified null handling** (R2 fix N4): previously, `initialJson === null`
 * short-circuited to `currentArray.length === 0`, while the string `"null"` or
 * `"[]"` or any malformed JSON went through `parsePortalHiddenArray` (which
 * returns `[]` for all of them). The two code paths could drift on edge-case
 * inputs. Now every input is normalised through `parsePortalHiddenArray` for
 * a single source of truth.
 *
 * Used by EditShareLinkSheet's `isDirty` check (Story 14.4 AC #9).
 */
export function arraysEqualConsideringNullInitial(
  initialJson: string | null,
  currentArray: string[]
): boolean {
  const initialArray = parsePortalHiddenArray(initialJson);
  if (initialArray.length !== currentArray.length) return false;
  const a = [...initialArray].sort();
  const b = [...currentArray].sort();
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

interface ApiError {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  errors?: Array<{ field: string; message: string }>;
}

async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ApiError;
    const message = err.detail ?? err.title ?? `Request failed (${res.status})`;
    const error = new Error(message) as Error & {
      status?: number;
      apiError?: ApiError;
    };
    error.status = res.status;
    error.apiError = err;
    throw error;
  }
  const json = (await res.json()) as { data: T };
  return json.data;
}

export async function listPortalLinks(
  clientId: string
): Promise<PortalLinkProjection[]> {
  const res = await apiGet(
    `/api/portal-links?clientId=${encodeURIComponent(clientId)}`
  );
  return unwrap<PortalLinkProjection[]>(res);
}

export async function getPortalLink(
  id: string
): Promise<PortalLinkProjection> {
  const res = await apiGet(`/api/portal-links/${id}`);
  return unwrap<PortalLinkProjection>(res);
}

export async function createPortalLink(
  input: CreatePortalLinkInput
): Promise<PortalLinkWithToken> {
  const res = await apiPost(`/api/portal-links`, input);
  return unwrap<PortalLinkWithToken>(res);
}

export async function updatePortalLink(
  id: string,
  input: UpdatePortalLinkInput
): Promise<PortalLinkProjection> {
  const res = await apiPatch(`/api/portal-links/${id}`, input);
  return unwrap<PortalLinkProjection>(res);
}

export async function revokePortalLink(id: string): Promise<void> {
  const res = await apiDelete(`/api/portal-links/${id}`);
  // 204 is included in res.ok (200-299 range); the previous "&& res.status !== 204"
  // guard was dead code. Any non-2xx response is treated as failure.
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as ApiError;
    throw new Error(err.detail ?? err.title ?? "Failed to revoke link");
  }
}

export async function regeneratePortalLinkToken(
  id: string
): Promise<PortalLinkWithToken> {
  const res = await apiPost(`/api/portal-links/${id}/regenerate-token`, {});
  return unwrap<PortalLinkWithToken>(res);
}

/**
 * Format a relative date string for "expires in X days" UX. Falls back to
 * absolute date if the diff is more than 90 days for clarity.
 */
export function formatExpirationLabel(
  expiresAt: number | null,
  now: number = Date.now()
): string {
  if (expiresAt === null) return "Never expires";
  const diff = expiresAt - now;
  if (diff <= 0) return "Expired";
  const days = Math.round(diff / 86_400_000);
  if (days <= 1) return "Expires today";
  if (days <= 30) return `Expires in ${days} days`;
  if (days <= 90) return `Expires in ${Math.round(days / 7)} weeks`;
  return `Expires ${new Date(expiresAt).toLocaleDateString()}`;
}
